import gql from "graphql-tag"
import React from "react"
import { useDispatch, useSelector } from "react-redux"
import { useLazyQuery } from "@apollo/client"
import { NextRegionSchemeWithId } from "@coast/utils"

import apolloClientNext from "../../../ApolloClientNext"
import { RootState } from "../../../store/configureStore"
import {
  CourseID,
  CoursePromo,
  CourseType,
  ProductPackageWithPrices,
} from "../../../types"
import {
  applyCouponToPackage,
  buildPackagePricesFromId,
  mapItemFieldsToEnums,
  maybeAddCourseIdToPackage,
  toPackageWithPrices,
  unApplyCouponToPackage,
} from "./utils"
import { logError, logErrorMessage } from "../../../utils/errors"
import { setCartId } from "../../../actions/cartActions"
import { setSnackbar } from "../../../actions/globalUiActions"
import {
  AddToCartDocument,
  ClearCouponDocument,
  ClearPackagesDocument,
  CompleteOrderWithSlotsDocument,
  CreateCartDocument,
  FetchCartDocument,
  GQLCartDetailsFragment,
  SetCouponDocument,
  SetStudentIdDocument,
} from "./CartProvider.graphqlTypes"
import {
  GQLProductPackageFieldsFragment,
  GQLProductPackageWithPriceInput,
} from "../../../graphqlTypes"
import { phoneNumber } from "../../../config"
import { track } from "../../../utils/analytics"
import { useCouponsContext } from "../../CouponsProvider"
import { useRegion } from "../../../hooks/useRegion"
import { isDefined } from "../../../utils/objects"
import { getCourseByType } from "../drivers-ed/content"

gql`
  fragment CartItemPackageWithPrices on ProductPackageWithPrice {
    id
    hasCourse
    hasCertificateShipping
    banner
    description
    extras
    hasLessons
    state
    title
    msrp
    price
    discountAmount
    couponDiscountAmount
    courseId
    courseType
    lessonPackage {
      id
      totalMinutes
      drivingMinutes
      observationMinutes
      numLessons
      numRoadTests
      isSharedLessons
    }
    didApplyCoupon
  }

  fragment CartDetails on Cart {
    id
    items {
      ...CartItemPackageWithPrices
    }
    isOrderComplete
    coupon {
      ... on CourseCoupon {
        id
        code
        discount
        title
        courseId
        state
        isDailyCoupon
        type
        expiration
      }
      ... on LessonCoupon {
        id
        code
        discount
        type
        title
        expiration
        regions {
          id
        }
        displayInLandingPages
        onlyFirstPurchase
      }
      ... on RoadTestCoupon {
        id
        code
        discount
        type
        title
        expiration
        regions {
          id
        }
        displayInLandingPages
        onlyFirstPurchase
      }
    }
    createdAt
    updatedAt
    bookedSlotIds
    paymentStatus
  }

  query FetchCart($cartId: ID!) {
    cart(id: $cartId) {
      ...CartDetails
    }
  }

  mutation CreateCart($input: CartCreateInput!) {
    createCart(input: $input) {
      ...CartDetails
    }
  }

  mutation AddToCart($input: IDLessonPackageInput!) {
    addToCart(input: $input) {
      ...CartDetails
    }
  }

  mutation ClearPackages($input: CartInput!) {
    clearPackages(input: $input) {
      ...CartDetails
    }
  }

  mutation CompleteOrderWithSlots($input: CompleteOrderInput!) {
    completeOrderWithSlots(input: $input) {
      ...CartDetails
    }
  }

  mutation SetStudentId($input: SetStudentInput!) {
    setStudentId(input: $input) {
      ...CartDetails
    }
  }

  mutation SetCoupon($input: SetCouponInput!) {
    setCoupon(input: $input) {
      ...CartDetails
    }
  }

  mutation ClearCoupon($input: CartInput!) {
    clearCoupon(input: $input) {
      ...CartDetails
    }
  }
`

/**
 * This handle the cart state, which is basically just what
 * package they have chosen.
 *
 * We persist this to Mongo via GraphQL/Next.
 */

interface CartContextProps {
  addToCart: (packageToAdd: ProductPackageWithPrices) => void
  replaceCartWithPackage: (
    packageToAdd: ProductPackageWithPrices,
  ) => Promise<void>
  computePricesFromZipAndAddToCart: (
    packageId: string,
    state: string,
    statePackages: GQLProductPackageFieldsFragment[],
    zipcode?: string,
    couponCode?: string,
    courseId?: CourseID,
    regionId?: string,
  ) => Promise<void>
  clearPackages: (options: { shouldFetchCart: boolean }) => Promise<void> | null
  completeOrder: (bookedSlotIds?: string[]) => Promise<void>
  setStudentId(studentId: string): void
  setCoupon(couponCode: string): void
  clearCoupon: () => void
  setIsLoading: (isLoading: boolean) => void
  fetchCart: () => Promise<void>
  id?: string
  state?: string
  region: NextRegionSchemeWithId | undefined
  packages?: ProductPackageWithPrices[]
  isLoading: boolean
  cartError: string | undefined
  isOrderComplete?: boolean
  couponCode?: string
  paymentStatus?: string | null
}

const CartContext = React.createContext<CartContextProps | undefined>(undefined)

function CartProvider(props: { children: React.ReactNode }) {
  const couponsState = useCouponsContext()
  const {
    cart: { cartId },
  } = useSelector((state: RootState) => state)
  const dispatch = useDispatch()
  const [cart, setCart] = React.useState<GQLCartDetailsFragment | undefined>(
    undefined,
  )
  const [state, setState] = React.useState<string | undefined>(undefined)
  const [isLoading, setIsLoading] = React.useState<boolean>(true)
  const [cartError, setCartError] = React.useState<string | undefined>(
    undefined,
  )
  const regionHook = useRegion(couponsState.address)

  function createCart(packageToAdd?: GQLProductPackageWithPriceInput) {
    return apolloClientNext
      .mutate({
        mutation: CreateCartDocument,
        variables: {
          input: { item: packageToAdd },
        },
      })
      .then((result) => {
        if (result.data?.createCart?.id) {
          dispatch(setCartId(result.data.createCart.id))
        }
      })
      .catch((error) => {
        logError(error)
        dispatch(setSnackbar("Something went wrong creating cart.", "error"))
      })
  }
  const [getCart] = useLazyQuery(FetchCartDocument, {
    client: apolloClientNext,
    fetchPolicy: "network-only",
    onError: async (error) => {
      if (error.message.includes("Could not find cart")) {
        await createCart()
        return
      }
      setCartError("Something went wrong fetching your cart.")
      logError(error)
    },
    onCompleted: (data) => {
      if (data?.cart?.items && data.cart.items.length > 0) {
        setCart(data.cart)
        setState(data.cart.items?.[0]?.state)
      }
      setIsLoading(false)
    },
  })

  React.useEffect(() => {
    if (cartId && (!cart || cart.id !== cartId)) {
      setIsLoading(true)
      getCart({ variables: { cartId } })
    } else {
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartId])

  const fetchCart = async () => {
    if (!cartId) {
      throw new Error("Cannot fetch cart without cartId.")
    }
    setIsLoading(true)
    await getCart({ variables: { cartId } })
  }

  function addToCart(packageToAdd: ProductPackageWithPrices) {
    track("Product Added", {
      cart_id: cartId,
      product_id: packageToAdd.id,
      price: packageToAdd.price,
    })

    if (!cartId || cart?.isOrderComplete) {
      return createCart(packageToAdd)
    }

    return apolloClientNext
      .mutate({
        mutation: AddToCartDocument,
        variables: {
          input: {
            id: cartId as string,
            item: packageToAdd,
          },
        },
      })
      .then(() => fetchCart())
      .catch((error) => {
        // This can happen if the cart is deleted from the DB. It should be
        // exceedingly rare.
        logError(error)
        dispatch(setCartId(undefined))
        setCart(undefined)
      })
  }

  function clearPackages(
    { shouldFetchCart }: { shouldFetchCart: boolean } = {
      shouldFetchCart: true,
    },
  ) {
    if (!cartId) return null
    if (cart?.isOrderComplete) {
      throw new Error("Cannot clear packages after order is complete.")
    }
    return apolloClientNext
      .mutate({
        mutation: ClearPackagesDocument,
        variables: {
          input: {
            id: cartId,
          },
        },
      })
      .then(() => (shouldFetchCart ? fetchCart() : undefined))
      .catch((error) => {
        if (error.message.includes("Cannot add to completed order")) {
          throw error
        }
        // TODO check which error. This can happen if it
        // can't find the cart, which is actually okay because we're probably
        // just adding a package, which will create a new cart.
        // eslint-disable-next-line no-console
        console.log("got an error we can probably ignore", error.message)
      })
  }

  async function replaceCartWithPackage(
    packageToAdd: ProductPackageWithPrices,
  ) {
    try {
      await clearPackages({ shouldFetchCart: false })
    } catch (error: any) {
      // This happens when cart.isOrderComplete is true and a new cart was not created.
      // So we want to create a new cart.
      return createCart(packageToAdd)
    }
    return addToCart(packageToAdd)
  }

  async function computePricesFromZipAndAddToCart(
    packageId: string,
    packageState: string,
    statePackages: GQLProductPackageFieldsFragment[],
    zipcode?: string,
    couponCode?: string,
    courseId?: CourseID,
    regionId?: string,
  ) {
    /**
     * TODO
     * This mimics the functionality of the old checkout flow, where we
     * just have an ID, state and zipcode. We may not need this in future.
     *
     * Also, computing prices doesn't seem to fit in this hook.
     */
    const { packageWithPrice, region } = await buildPackagePricesFromId(
      couponsState,
      packageId,
      packageState,
      statePackages,
      zipcode,
    )

    let course: CoursePromo | undefined

    if (
      !courseId &&
      packageWithPrice?.hasCourse &&
      packageWithPrice.courseType
    ) {
      course = getCourseByType(
        packageState,
        packageWithPrice.courseType as CourseType,
        regionId || region?.id,
      )

      if (!course) {
        logErrorMessage(
          `Could not determine the course for package ${packageId} in state ${packageState} and zip ${zipcode}.`,
        )
      }
    }

    if (!packageWithPrice) {
      const errorMessage = `No package found for id ${packageId} with state ${packageState} and zip ${zipcode}.`
      logErrorMessage(errorMessage)
      setCartError(errorMessage)
      return undefined
    }

    if (!isDefined(packageWithPrice.price)) {
      const errorMessage =
        `Package price not set for ${packageId}` +
        ` in state ${packageState} and zip ${zipcode},` +
        ` region: ${region}.`
      logErrorMessage(errorMessage)
      setCartError(errorMessage)
      return undefined
    }

    const packageWithCoupon = applyCouponToPackage(
      couponsState,
      packageWithPrice,
      couponCode,
      regionId,
    )
    const packageWithCourseId = maybeAddCourseIdToPackage(
      packageWithCoupon,
      courseId || course?.courseId,
    )

    return replaceCartWithPackage(packageWithCourseId)
  }

  async function completeOrder(bookedSlotIds?: string[]) {
    if (!cartId) return
    try {
      await apolloClientNext.mutate({
        mutation: CompleteOrderWithSlotsDocument,
        variables: {
          input: {
            id: cartId,
            ...(bookedSlotIds ? { bookedSlotIds } : {}),
          },
        },
      })
    } catch (error) {
      logError(error)
      dispatch(
        setSnackbar(
          `Something went wrong completing your order. Please do not try your payment again. Call us at ${phoneNumber} and we will help you out.`,
          "error",
        ),
      )
    }

    await fetchCart()
    // Clear out the cart ID for the next time.
    dispatch(setCartId(undefined))
    setCart(undefined)
  }

  function setStudentId(studentId: string) {
    if (!cartId) return
    apolloClientNext
      .mutate({
        mutation: SetStudentIdDocument,
        variables: {
          input: {
            id: cartId,
            studentId,
          },
        },
      })
      .then(() => fetchCart())
      .catch((error) => {
        logError(error)
        dispatch(
          setSnackbar("Something went wrong updating your order.", "error"),
        )
      })
  }

  function setCoupon(couponCode: string) {
    if (!cartId) return
    apolloClientNext
      .mutate({
        mutation: SetCouponDocument,
        variables: {
          input: {
            cartId,
            couponCode,
          },
        },
      })
      .then(async () => {
        const item = cart?.items[0]

        if (!item) {
          return fetchCart()
        }

        const pack = toPackageWithPrices(item)
        const packageWithCoupon = applyCouponToPackage(
          couponsState,
          pack,
          couponCode,
          regionHook.data?.id,
        )
        return replaceCartWithPackage(packageWithCoupon)
      })
      .catch((error) => {
        logError(error)
        dispatch(
          setSnackbar("Something went wrong applying your coupon.", "error"),
        )
      })
  }

  function clearCoupon() {
    if (!cartId) return
    apolloClientNext
      .mutate({
        mutation: ClearCouponDocument,
        variables: {
          input: {
            id: cartId,
          },
        },
      })
      .then(async () => {
        const item = cart?.items[0]

        if (!item) {
          return fetchCart()
        }

        const pack = toPackageWithPrices(item)
        const unalteredItem = await unApplyCouponToPackage(pack)

        return replaceCartWithPackage(unalteredItem)
      })
      .catch((error) => {
        logError(error)
        dispatch(
          setSnackbar("Something went wrong applying your coupon.", "error"),
        )
      })
  }

  const value = {
    addToCart,
    replaceCartWithPackage,
    computePricesFromZipAndAddToCart,
    completeOrder,
    clearPackages,
    setStudentId,
    setCoupon,
    clearCoupon,
    setIsLoading,
    fetchCart,
    id: cart?.id,
    state,
    region: regionHook.data ?? undefined,
    packages: cart?.items?.map(mapItemFieldsToEnums) || undefined,
    couponCode: cart?.coupon?.code || undefined,
    isOrderComplete: !!cart?.isOrderComplete,
    isLoading,
    cartError,
    paymentStatus: cart?.paymentStatus,
  }

  return <CartContext.Provider value={value} {...props} />
}

function useCart() {
  const context = React.useContext(CartContext)
  if (!context) {
    throw new Error("useCart must be used within a CartProvider")
  }
  return context
}

export { CartProvider, useCart }
